import React from 'react';

import { Flex, InputNumber, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { EpdDocumentKind, EpdState } from '@app/models/orders';
import { formatDate } from '@app/services/date';
import { useEpdStateName } from '@app/services/hooks/useEpdStateName';
import { FilterBlock, IField } from '@components/FilterBlock';
import { DateRangeFilter, StatusFilterSelect } from '@pages/CounterpartyOrders/components/OrdersFilters/FilterFields';

import { IEpdFilters } from '../../interfaces';

interface IProps {
  kind: EpdDocumentKind;
  total?: number;
  onChange: (filters: IEpdFilters, restored: boolean) => void;
}

interface IFilterValues {
  state?: { value?: EpdState[] };
  date?: { from?: string; to?: string };
  order_num?: number | null;
  awaiting_me?: string;
  discrepancy?: string;
}

export const EpdFilters: React.FC<IProps> = ({ kind, total, onChange }) => {
  const epdT = useTranslation(NSKeys.epd);
  const stateName = useEpdStateName(kind);
  const commonT = useTranslation(NSKeys.common);
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  const opened = React.useRef(false);
  React.useEffect(() => {
    opened.current = true;
    return () => {
      opened.current = false;
    };
  }, []);
  const fields: IField[] = [
    {
      name: 'state',
      label: epdT.t('state'),
      inputField: (
        <StatusFilterSelect
          options={Object.values(EpdState).map((state) => ({
            value: state,
            label: stateName(state),
          }))}
        />
      ),
      renderDisplayValue: (value: IFilterValues['state']) =>
        value?.value?.map((state) => stateName(state)).join(', ') || '',
    },
    {
      name: 'date',
      label: epdT.t('documentDate'),
      inputField: <DateRangeFilter />,
      renderDisplayValue: (value: IFilterValues['date']) => `${formatDate(value?.from)} — ${formatDate(value?.to)}`,
    },
    {
      name: 'order_num',
      label: epdT.t('orderNumber'),
      inputField: <InputNumber min={1} precision={0} />,
      renderDisplayValue: (value) => String(value ?? ''),
    },
    {
      name: 'awaiting_me',
      label: epdT.t('awaitingMe'),
      inputField: (
        <Select allowClear options={[{ value: 'true', label: commonT.t('yes') }]} style={{ minWidth: 120 }} />
      ),
      renderDisplayValue: () => commonT.t('yes'),
    },
    {
      name: 'discrepancy',
      label: epdT.t('discrepancy'),
      inputField: (
        <Select allowClear options={[{ value: 'true', label: commonT.t('yes') }]} style={{ minWidth: 120 }} />
      ),
      renderDisplayValue: () => commonT.t('yes'),
    },
  ];
  return (
    <Flex justify="space-between" align="center" gap={16}>
      <FilterBlock
        uidPrefix={`epd-${kind}`}
        fields={fields}
        showSearchField={false}
        initialValues={{}}
        onSubmitValues={(values: IFilterValues) =>
          onChange(
            {
              state: values.state?.value?.length ? values.state.value : undefined,
              date_from: values.date?.from || undefined,
              date_to: values.date?.to || undefined,
              order_num: values.order_num || undefined,
              awaiting_me: values.awaiting_me === 'true',
              discrepancy: values.discrepancy === 'true',
            },
            !opened.current
          )
        }
      />
      {total !== undefined && (
        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          {ordersT.t('filters.total', { count: total })}
        </Typography.Text>
      )}
    </Flex>
  );
};
